import _ from 'lodash';
import Resizer from 'react-image-file-resizer';

export const fileSizes = [600, 200]
export const manifestSizes = [72, 96, 128, 144, 152, 192, 384, 512]
export const thumbnailSizes = [200]

export const manifestSizesT = {
  72: 72,
  96: 96,
  128: 128,
  144: 144,
  152: 152,
  192: 192,
  384: 384,
  512: 512
}

const _imageReduceTypes = {
  manifest: 'manifest',
  portrait: 'portrait',
  thumbnail: 'thumbnail',
  full: 'full'
}

const reduceImage = (
  file,
  {
    imageType = _imageReduceTypes.thumbnail,  // manifest | thumbnail | portrait | full

    // manifest
    size = 300,

    // thumbnail
    maxSize = 500,

    // portrait
    portraitHeight = 900,
    portraitMaxWidth = 700,

    // full
    fullMax = 1400

  } = {}
) => {

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        let canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d');

        // =====================================================
        // 📌 MANIFEST — ALWAYS SQUARE
        // =====================================================
        if (imageType === _imageReduceTypes.manifest) {

          canvas.width = size;
          canvas.height = size;

          ctx.clearRect(0, 0, size, size);

          const scale = Math.min(size / img.width, size / img.height);
          const newW = img.width * scale;
          const newH = img.height * scale;

          const offsetX = (size - newW) / 2;
          const offsetY = (size - newH) / 2;

          ctx.drawImage(img, offsetX, offsetY, newW, newH);

          canvas.toBlob(
            blob => {
              blob.name = `manifest_${size}.png`;
              resolve(blob);
            },
            'image/png',
            1
          );

          // =====================================================
          // 📌 PORTRAIT — KEEP RATIO, BUT NARROWER
          // =====================================================
        } else if (imageType === _imageReduceTypes.portrait) {

          const scale = Math.min(
            portraitMaxWidth / img.width,
            portraitHeight / img.height
          );

          const newW = img.width * scale;
          const newH = img.height * scale;

          canvas.width = newW;
          canvas.height = newH;

          ctx.drawImage(img, 0, 0, newW, newH);

          canvas.toBlob(
            blob => {
              blob.name = `portrait_${Math.round(newW)}x${Math.round(newH)}.jpg`;
              resolve(blob);
            },
            'image/jpeg',
            0.9
          );

          // =====================================================
          // 📌 FULL — PHONE-READY SIZE, KEEP RATIO
          // =====================================================
        } else if (imageType === _imageReduceTypes.full) {

          const scale = Math.min(
            fullMax / img.width,
            fullMax / img.height
          );

          const newW = img.width * scale;
          const newH = img.height * scale;

          canvas.width = newW;
          canvas.height = newH;

          ctx.drawImage(img, 0, 0, newW, newH);

          canvas.toBlob(
            blob => {
              blob.name = `full_${Math.round(newW)}.jpg`;
              resolve(blob);
            },
            'image/jpeg',
            0.9
          );

          // =====================================================
          // 📌 THUMBNAIL — SMALLER, KEEP RATIO
          // =====================================================
        } else {

          const scale = Math.min(
            maxSize / img.width,
            maxSize / img.height
          );

          const newW = img.width * scale;
          const newH = img.height * scale;

          canvas.width = newW;
          canvas.height = newH;

          ctx.drawImage(img, 0, 0, newW, newH);

          canvas.toBlob(
            blob => {
              blob.name = `thumb_${Math.round(newW)}.jpg`;
              resolve(blob);
            },
            'image/jpeg',
            0.9
          );
        }
      };

      img.onerror = reject;
      img.src = event.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};




/** Creates an icon for each of the `manifestSizes` */
export const createManifestIcons = async (file) => {
  const promises = []
  _.forEach(manifestSizes, (ms, msi) => {
    promises.push(reduceImage(file, { imageType: _imageReduceTypes.manifest, size: ms }))
  })
  return Promise.all(promises)
}

/** Creates an icon for each of the `thumbnailSizes` */
export const createThumbnailIcons = async (file, autoReduceImage, isPortrait, keepOriginalSize) => {
  const promises = []
  if (autoReduceImage) {
    _.forEach(fileSizes, (fs, fsi) => {
      promises.push(reduceImage(file, { imageType: fsi === 1 ? _imageReduceTypes.thumbnail : _imageReduceTypes.full, size: fs }))
    })
  } else {
    _.forEach(thumbnailSizes, (tns, tnsi) => {
      promises.push(reduceImage(file, { imageType: tnsi === 1 ? _imageReduceTypes.thumbnail : _imageReduceTypes.full, size: isPortrait, tns }))
    })
  }
  return Promise.all(promises)
}

export const createThumbnail = async (file, size, keepOriginalSize) => {
  const promises = []
  promises.push(reduceImage(file, { imageType: _imageReduceTypes.thumbnail, size: size }))
  return Promise.all(promises)
}